import { z } from "zod"

export const ResolveEngagementMailBodySchema = z.object({
  outcome: z.enum(["sent", "suppressed", "not_sent"]),
  providerId: z.string().trim().min(1).max(200).optional(),
}).refine((body) => body.outcome !== "sent" || Boolean(body.providerId), {
  message: "Record the provider reference before marking mail sent.", path: ["providerId"],
})
import { EngagementStateSchema } from "./engagements.js"
import { PortalRequestResponseSchema } from "./portal.js"

export const EngagementSummarySchema = z.object({
  engagementId: z.uuid(),
  workspaceId: z.uuid(),
  serviceDescription: z.string(),
  state: EngagementStateSchema,
  activatedAt: z.iso.datetime().nullable(),
  kickoffRequestId: z.uuid().nullable(),
  ready: z.boolean(),
  blockers: z.array(z.object({ itemId: z.uuid(), reason: z.string() })),
  requests: z.array(PortalRequestResponseSchema),
})
export const EngagementListResponseSchema = z.object({ engagements: z.array(EngagementSummarySchema) })
export type EngagementSummary = z.infer<typeof EngagementSummarySchema>
export const EngagementActivationResponseSchema = z.object({
  workspaceId: z.uuid().nullable(),
  contactId: z.uuid().nullable(),
  engagementId: z.uuid().nullable(),
  requestId: z.uuid().nullable(),
  invitationStatus: z.enum(["queued", "sent", "failed", "not_sent", "no_client_invited"]),
  /** The client's readable link segment; null when the client has no link. */
  clientSlug: z.string().nullable().optional(),
  /** The link the firm hands out, in the firm's link style. Null when the
      client has no link. */
  linkUrl: z.string().url().nullable().optional(),
})
export const EngagementSettingsSchema = z.object({
  timeZone: z.string(),
  sendHour: z.number().int().min(0).max(23),
  engagementEnabled: z.boolean(),
  recurrenceEnabled: z.boolean(),
  accountantEmail: z.email().nullable(),
  quickbooksFirmId: z.string().nullable(),
  accessInstructions: z.string().nullable(),
  supportName: z.string().nullable(),
  supportEmail: z.email().nullable(),
  supportPhone: z.string().nullable(),
  approvedHandoffDomains: z.array(z.string()),
})
export const EngagementSettingsBodySchema = EngagementSettingsSchema.omit({ timeZone: true, sendHour: true }).partial().extend({
  accountantEmail: z.email().max(254).nullable().optional(),
  quickbooksFirmId: z.string().trim().max(120).nullable().optional(),
  accessInstructions: z.string().trim().max(2000).nullable().optional(),
  supportName: z.string().trim().max(120).nullable().optional(),
  supportEmail: z.email().max(254).nullable().optional(),
  supportPhone: z.string().trim().max(60).nullable().optional(),
  approvedHandoffDomains: z.array(z.string().toLowerCase().regex(/^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/)).max(30).optional(),
})
export const EngagementDeliverySchema = z.object({
  deliveryId: z.uuid(),
  seriesId: z.uuid(),
  version: z.number().int().positive(),
  requestId: z.uuid().nullable(),
  title: z.string(),
  note: z.string().nullable(),
  publishedAt: z.iso.datetime(),
  acknowledgedByMe: z.boolean().optional(),
  files: z.array(z.object({ fileId: z.uuid(), fileName: z.string(), byteSize: z.number(), available: z.boolean() })),
  acknowledgedBy: z.array(z.object({ contactId: z.uuid(), name: z.string(), acknowledgedAt: z.iso.datetime() })),
  notificationStatus: z.enum(["pending", "sending", "sent", "failed", "suppressed"]),
})
export const EngagementDeliveryListSchema = z.object({ deliveries: z.array(EngagementDeliverySchema) })
export const EngagementDeliveryPreviewBodySchema = z.object({
  requestId: z.uuid().nullable(), engagementId: z.uuid().nullable().optional(), fileIds: z.array(z.uuid()).min(1).max(50),
})
export const EngagementDeliveryPreviewSchema = z.object({
  requestId: z.uuid().nullable(), recipients: z.array(z.object({ contactId: z.uuid(), email: z.email() })),
  files: z.array(z.object({ fileId: z.uuid(), fileName: z.string(), byteSize: z.number() })),
  ready: z.boolean(), blockers: z.array(z.object({ itemId: z.uuid(), reason: z.string() })),
  expectedVersions: z.array(z.object({ itemId: z.uuid(), evidenceVersion: z.number().int().positive() })),
})
export type EngagementDelivery = z.infer<typeof EngagementDeliverySchema>

export const EngagementScheduleSchema = z.object({
  scheduleId: z.uuid(), engagementId: z.uuid(), templateId: z.uuid(), frequency: z.literal("monthly"),
  timeZone: z.string(), firstCollectionMonth: z.string(), requestDay: z.number(), sendHour: z.number(), dueDay: z.number(),
  paused: z.boolean(), lastCollectionMonth: z.string().nullable(), nextRunAt: z.iso.datetime(),
  blockedReason: z.string().nullable(), conflictingRequestId: z.uuid().nullable(), skippedMonths: z.array(z.string()),
})
export const EngagementScheduleListSchema = z.object({ schedules: z.array(EngagementScheduleSchema) })
export type EngagementSchedule = z.infer<typeof EngagementScheduleSchema>

export const PublishEngagementDeliveryBodySchema = z.object({
  idempotencyKey: z.uuid(), requestId: z.uuid().nullable(), engagementId: z.uuid().nullable().optional(),
  seriesId: z.uuid().optional(), expectedPreviousVersion: z.number().int().positive().optional(),
  title: z.string().trim().min(1).max(120), note: z.string().trim().max(2000).optional(),
  fileIds: z.array(z.uuid()).min(1).max(50),
  expectedVersions: z.array(z.object({ itemId: z.uuid(), evidenceVersion: z.number().int().positive() })).max(50),
  confirmRecipients: z.literal(true),
})

export const InviteEngagementContactBodySchema = z.object({
  idempotencyKey: z.uuid(), name: z.string().trim().min(1).max(120), email: z.email().max(254), sharedAccessConfirmed: z.literal(true),
  /** False adds the person without an email; send the invite later. */
  sendInvitation: z.boolean().default(true),
})

export const EngagementFileSchema = z.object({
  fileId: z.uuid(), fileName: z.string(), byteSize: z.number(), contentType: z.string(),
  direction: z.enum(["client_upload", "firm_shared"]), status: z.string(),
  engagementId: z.uuid().nullable(), requestId: z.uuid().nullable(), requestTitle: z.string().nullable(),
  requestItemId: z.uuid().nullable(), coverageStart: z.iso.date().nullable(), coverageEnd: z.iso.date().nullable(),
  uploadedAt: z.iso.datetime(), updatedAt: z.iso.datetime(), available: z.boolean(), deliveryStaged: z.boolean(),
})
export const EngagementFileListSchema = z.object({ files: z.array(EngagementFileSchema), hasMore: z.boolean(), page: z.number().int() })
export type EngagementFile = z.infer<typeof EngagementFileSchema>
export const EngagementFileFilterSchema = z.object({
  engagementId: z.uuid().optional(), requestId: z.uuid().optional(),
  coverageFrom: z.iso.date().optional(), coverageTo: z.iso.date().optional(),
  unknownCoverage: z.enum(["true", "false"]).optional(),
  direction: z.enum(["client_upload", "firm_shared"]).optional(),
  search: z.string().trim().max(120).optional(), page: z.coerce.number().int().min(0).max(10000).default(0),
})
export const EngagementFileCoverageBodySchema = z.object({
  coverageStart: z.iso.date().nullable(), coverageEnd: z.iso.date().nullable(), expectedUpdatedAt: z.iso.datetime(),
}).refine((body) => !body.coverageStart || !body.coverageEnd || body.coverageStart <= body.coverageEnd,
  "Coverage end must follow coverage start.")
